import React, { useEffect, useState } from "react";
import { Paper, Typography, TablePagination, FormControl, InputLabel, Select, MenuItem, IconButton, CircularProgress, Box } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import axios from "axios";
import Table from "../Table/Table";

const ViewUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("last_modified");
  const [filterType, setFilterType] = useState("All");
  const [loading, setLoading] = useState(true); // New loading 
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const tableHeaders = [
    { dbParam: "index", displayName: "Sr. No." },
    { dbParam: "supplierName", displayName: "Supplier Name" },
    { dbParam: "name", displayName: "Uploaded File Name" },
    { dbParam: "dataType", displayName: "Data Type" },
    { dbParam: "last_modified", displayName: "Last Modified" },
    { dbParam: "download", displayName: "Download" },
  ];

  useEffect(() => {
    const fetchUploadedFiles = async () => {
      const projectId = localStorage.getItem("selectedProject");
      if (!projectId) {
        console.error("No projectId found in local storage.");
        setLoading(false); // Stop loading if no projectId
        return;
      }

      try {
        setLoading(true); // Start loading
        const response = await axios.get(
          `${baseURL}get_file_names?projectId=${projectId}`
        );
        const data = response.data;

        let processedFiles = [];
        if (data.projectId === projectId) {
          Object.keys(data.suppliers).forEach((supplierName) => {
            const { flash_files: flashFiles, pdi_files: pdiFiles, packing_files: packingFiles, el_files: elFiles } = data.suppliers[supplierName];

            flashFiles.forEach((file) => {
              processedFiles.push({
                supplierName,
                name: file.file_path.split("/").pop(),
                dataType: "Flash",
                last_modified: file.last_modified,
                download_url: file.download_url,
              });
            });

            pdiFiles.forEach((file) => {
              processedFiles.push({
                supplierName,
                name: file.file_path.split("/").pop(),
                dataType: "PDI",
                last_modified: file.last_modified,
                download_url: file.download_url,
              });
            });

            packingFiles.forEach((file) => {
              processedFiles.push({
                supplierName,
                name: file.file_path.split("/").pop(),
                dataType: "Packing List",
                last_modified: file.last_modified,
                download_url: file.download_url,
              });
            });

            elFiles.forEach((file) => {
              processedFiles.push({
                supplierName,
                name: file.file_path.split("/").pop(),
                dataType: "EL",
                last_modified: file.last_modified,
                download_url: file.download_url,
              });
            });
          });
        }

        processedFiles = processedFiles.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
        processedFiles = processedFiles.map((file, index) => ({
          ...file,
          index: index + 1,
        }));

        setUploadedFiles(processedFiles);
        setFilteredFiles(processedFiles);
      } catch (error) {
        console.error("Error fetching uploaded files:", error);
      } finally {
        setLoading(false); // Stop loading after fetch completes or fails
      }
    };

    fetchUploadedFiles();
  }, []);

  const handleFilterChange = (event) => {
    const selectedType = event.target.value;
    setFilterType(selectedType);
    setPage(0);

    if (selectedType === "All") {
      setFilteredFiles(uploadedFiles);
    } else {
      const filtered = uploadedFiles.filter((file) => file.dataType === selectedType);
      setFilteredFiles(filtered);
    }
  };

  const handleSort = (property) => {
    if (property === "download") return;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sortedFiles = [...filteredFiles].sort((a, b) => {
      const valA = a[property];
      const valB = b[property];
      if (property === "last_modified") {
        return isAsc
          ? new Date(valA) - new Date(valB)
          : new Date(valB) - new Date(valA);
      }
      return isAsc ? (valA < valB ? -1 : 1) : (valA > valB ? -1 : 1);
    });

    setFilteredFiles(sortedFiles);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = (downloadUrl) => {
    window.open(downloadUrl, "_blank");
  };

  return (
    <Paper elevation={3} style={{ padding: "16px" }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <Typography variant="h6">Uploaded Files</Typography>
            <FormControl style={{ minWidth: 150 }}>
              <InputLabel>Data Type</InputLabel>
              <Select value={filterType} onChange={handleFilterChange} label="Data Type">
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Flash">Flash</MenuItem>
                <MenuItem value="PDI">PDI</MenuItem>
                <MenuItem value="Packing List">Packing List</MenuItem>
                <MenuItem value="EL">EL</MenuItem>
              </Select>
            </FormControl>
          </div>
          <Table
            tableHeaders={tableHeaders}
            tableData={filteredFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((file) => ({
              ...file,
              download: (
                <IconButton
                  color="primary"
                  sx={{
                    color: '#424752',
                    '&:hover': { color: '#6c757d' },
                  }}
                  onClick={() => handleDownload(file.download_url)}
                >
                  <DownloadIcon />
                </IconButton>
              ),
            }))}
            order={order}
            orderBy={orderBy}
            handleSort={handleSort}
            handleEditClick={() => {}}
          />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredFiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
};

export default ViewUpload;